const headers = { "Content-Type": "application/json" };

export async function fetchImagingStudies(
  slidesInProject,
  dateStart,
  dateEnd,
  organizationId,
  next,
  paging,
  getReferences = false
) {
  const count = paging.count || 10;
  let url =
    process.env.VUE_APP_FHIR_BASE +
    `ImagingStudy?_include=ImagingStudy:endpoint&_count=${count}`;
  if (dateStart) url += `&started=ge${dateStart}`;
  if (dateEnd) url += `&started=le${dateEnd}`;
  if (organizationId) url += `&image-owner=Organization/${organizationId}`;
  if (getReferences) url += `&_include=ImagingStudy:subject`;

  if (next) {
    url = next;
  }

  const bundle = await (
    await fetch(url, {
      method: "get",
      headers
    })
  ).json();

  // if no search results exit here
  if (bundle.total === 0) {
    return {
      imagingStudy: [],
      endpoint: [],
      organizations: [],
      subjects: [],
      specimens: []
    };
  }

  let nextPage = bundle.link.find(e => e.relation === "next");
  if (nextPage) nextPage = nextPage.url;
  let previousPage = bundle.link.find(e => e.relation === "previous");
  if (previousPage) previousPage = previousPage.url;

  let filter = (entry, resource) =>
    entry
      .filter(e => e.resource.resourceType === resource)
      .map(e => e.resource);

  let is = filter(bundle.entry, "ImagingStudy");
  // filter slides which are already in slidesInProject
  if (slidesInProject) {
    // TODO those slides are only the one loaded. if there are other in a paged search results not retrieved yet, this is not working
    for (let s of slidesInProject) {
      let ib = is.filter(
        e => e.identifier[0].value === s.fhir_identifier.substr(14)
      );
      ib.forEach(e => (e.inProject = true));
    }
  }
  let endpoints = filter(bundle.entry, "Endpoint");
  // get an array of unique organization references from imagingstudies
  let organizations = null;
  if (is.length > 0) {
    organizations = [
      ...new Set(
        is
          .map(e => e.extension)[0]
          .filter(
            e =>
              e.url ===
              "http://deeppath.cl/fhir/StructureDefinition/ext-image-owner"
          )
          .map(e => e.valueReference.reference)
      )
    ].map(e => fetchResource(e));

    organizations = await Promise.all(organizations);
  }
  let subjects = filter(bundle.entry, "Patient");
  // Cannot retrieve specimen from ImagingStudy, needs :iterate search parameter
  // let specimens = filter(bundle.entry, "Specimen");
  let specimens = null;
  if (getReferences && is.length > 0) {
    specimens = is.map(
      is =>
        is.series.map(series =>
          series.specimen.map(specimen => fetchResource(specimen.reference))
        )[0][0]
    );
    specimens = await Promise.all(specimens);
  }
  for (let i of is) {
    await prepareImagingStudy(i);
  }
  // let specimen = [
  //   ...new Set(
  //     is
  //       .map(e => e.extension)[0]
  //       .filter(
  //         e =>
  //           e.url ===
  //           "http://deeppath.cl/fhir/StructureDefinition/ext-image-owner"
  //       )
  //       .map(e => e.valueReference.reference)
  //   )
  // ].map(e => fetchResource(e));
  // specimen = await Promise.all(specimen);

  return {
    imagingStudy: is,
    endpoint: endpoints,
    organizations,
    subjects,
    specimens,
    paging: {
      next: nextPage,
      previous: previousPage,
      count: paging.count,
      total: bundle.total
    }
  };
}

export function prepareImagingStudy(i) {
  let reference = i.extension.find(
    e => e.url === "http://deeppath.cl/fhir/StructureDefinition/ext-image-owner"
  ).valueReference.reference;
  i.organizationResource = {
    reference,
    promise: fetchResource(reference)
  };

  reference = i.endpoint[0].reference;
  i.endpointResource = {
    reference,
    promise: fetchResource(reference)
  };

  reference = i.series[0].specimen[0].reference;
  i.specimenResource = {
    promise: fetchResource(reference),
    reference: reference
  };

  return i;
}

export async function fetchOrganizationWithEndpoint(
  identifier,
  query,
  endpointCode
) {
  const search = encodeURIComponent(identifier);
  let url =
    process.env.VUE_APP_FHIR_BASE + `Organization/?identifier=${search}`;
  if (query) {
    url += `&${query}`;
  }
  const bundle = await (
    await fetch(url, {
      method: "get"
      // headers
    })
  ).json();
  if (bundle.total === 1) {
    const organization = bundle.entry.find(
      e => e.resource.resourceType === "Organization"
    ).resource;

    let endpoint = bundle.entry.find(
      e =>
        e.resource.resourceType === "Endpoint" &&
        e.resource.connectionType.code === endpointCode
    );
    if (endpoint) endpoint = endpoint.resource;
    return { organization, endpoint };
  }

  return null;
}

export async function fetchResourceWithIdentifier(resource, identifier) {
  const search = encodeURIComponent(identifier);
  const url =
    process.env.VUE_APP_FHIR_BASE + resource + `/?identifier=${search}`;
  const bundle = await (
    await fetch(url, {
      method: "get"
      // headers
    })
  ).json();
  if (bundle.total === 1) {
    return bundle.entry[0].resource;
  }

  return null;
}

export async function fetchResource(reference) {
  return await (
    await fetch(process.env.VUE_APP_FHIR_BASE + reference, {
      method: "get",
      headers
    })
  ).json();
}

export async function searchOrganization(name) {
  const bundle = await (
    await fetch(
      process.env.VUE_APP_FHIR_BASE + `Organization?name=${name}&_summary=true`,
      {
        method: "get",
        headers
      }
    )
  ).json();

  if (bundle.total === 0) return [];
  return bundle.entry.map(e => e.resource);
}

export async function fetchObservations(
  specimenId,
  practitionerIdentifier,
  next
) {
  let resources = [];
  let url =
    process.env.VUE_APP_FHIR_BASE + `Observation?specimen=${specimenId}`;
  if (practitionerIdentifier) {
    url += `&performer.identifier=${encodeURIComponent(
      practitionerIdentifier
    )}`;
  }
  if (next) url = next;

  const bundle = await (
    await fetch(url, {
      method: "get",
      headers
    })
  ).json();

  if (bundle.link !== undefined) {
    // enable retrieving from paged search results
    const next = bundle.link.find(e => e.relation === "next");
    if (next !== undefined) {
      resources = resources.concat(
        await fetchObservations(specimenId, practitionerIdentifier, next.url)
      );
    }
  }
  if (bundle.total === 0) return [];
  resources = resources.concat(bundle.entry.map(e => e.resource));
  return resources;
}

export async function deleteResource(resource, id) {
  return await (
    await fetch(process.env.VUE_APP_FHIR_BASE + `${resource}/${id}`, {
      method: "delete",
      headers
    })
  ).json();
}

export async function countNumberOfAnnotationsForSlide(specimenId) {
  return await (
    await (
      await fetch(
        process.env.VUE_APP_FHIR_BASE +
          `Observation?specimen=${specimenId}&_summary=count`,
        {
          method: "get",
          headers
        }
      )
    ).json()
  ).total;
}

export async function countNumberOfAnnotationsForAnnotationClass(
  annotationClassID
) {
  return await (
    await (
      await fetch(
        process.env.VUE_APP_FHIR_BASE +
          `Observation?code=${annotationClassID}&_summary=count`,
        {
          method: "get",
          headers
        }
      )
    ).json()
  ).total;
}
